// MorrowOS CogniMemo client helpers.
//
// Reads the server proxy at:
//   GET /api/morrow/memory?q=<optional search>
//
// The key stays on the server (`COGNIMEMO_API_KEY`). Neither call throws — a
// failed fetch resolves to a structured payload so the memory UI keeps
// rendering.

import { apiUrl } from "./apiBase";
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";

export type MemoryScope = "personal" | "org" | "all";

async function memoryAuthHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = await getClerkToken().catch(() => null);
  if (token) headers["x-clerk-token"] = token;
  const ownerId = getOwnerId();
  if (ownerId) headers["x-morrow-owner-id"] = ownerId;
  return headers;
}

export interface MorrowMemory {
  readonly id: string;
  readonly text: string;
  readonly type: string | null;
  readonly createdAt: string | null;
  readonly score: number | null;
  readonly department: string | null;
  readonly ownerEmail: string | null;
  readonly threadId: string | null;
  readonly toolSlug: string | null;
}

export interface MorrowMemoryPayload {
  readonly configured: boolean;
  readonly memories: readonly MorrowMemory[];
  readonly error: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function metadataRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readMetadataString(rec: Record<string, unknown>, ...keys: string[]): string | null {
  const metadata = metadataRecord(rec.metadata) ?? metadataRecord(rec.meta);
  for (const key of keys) {
    const direct = asString(rec[key]);
    if (direct) return direct;
    if (metadata) {
      const nested = asString(metadata[key]);
      if (nested) return nested;
    }
  }
  return null;
}

export function parseMemory(value: unknown, index: number): MorrowMemory | null {
  if (!value || typeof value !== "object") return null;
  const rec = value as Record<string, unknown>;
  const text =
    asString(rec.text) ??
    asString(rec.content) ??
    asString(rec.memory) ??
    asString(rec.body) ??
    asString(rec.summary);
  if (!text) return null;
  return {
    id: asString(rec.id) ?? asString(rec.memory_id) ?? `memory-${index}`,
    text,
    type: asString(rec.type) ?? asString(rec.category) ?? asString(rec.kind),
    createdAt:
      asString(rec.createdAt) ?? asString(rec.created_at) ?? asString(rec.timestamp) ?? null,
    score: asNumber(rec.score) ?? asNumber(rec.relevance),
    department: readMetadataString(rec, "department", "dept", "team"),
    ownerEmail: readMetadataString(rec, "ownerEmail", "owner_email", "user_email", "email"),
    threadId: readMetadataString(rec, "threadId", "thread_id", "source_thread"),
    toolSlug: readMetadataString(rec, "toolSlug", "tool_slug", "tool", "integration"),
  };
}

export function parseMemoryPayload(value: unknown): MorrowMemoryPayload {
  const rec = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const rawList = Array.isArray(rec.memories)
    ? rec.memories
    : Array.isArray(rec.results)
      ? rec.results
      : Array.isArray(rec.data)
        ? rec.data
        : [];
  return {
    configured: rec.configured !== false,
    memories: rawList
      .map((entry, index) => parseMemory(entry, index))
      .filter((entry): entry is MorrowMemory => entry !== null),
    error: asString(rec.error),
  };
}

export async function fetchMemories(
  query = "",
  scope: MemoryScope = "all",
): Promise<MorrowMemoryPayload> {
  try {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (scope !== "all") params.set("scope", scope);
    const suffix = params.size > 0 ? `?${params.toString()}` : "";
    const res = await fetch(apiUrl(`/api/morrow/memory${suffix}`), {
      headers: await memoryAuthHeaders(),
    });
    if (!res.ok) {
      return {
        configured: true,
        memories: [],
        error: `Memory endpoint returned ${res.status}`,
      };
    }
    return parseMemoryPayload(await res.json());
  } catch (err) {
    console.warn("[memory] fetch error", err);
    return {
      configured: true,
      memories: [],
      error: "Memory endpoint is unavailable.",
    };
  }
}
